import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useContext, useRef } from 'react'
import { isAxiosError } from 'axios'
import dayjs from 'dayjs'

import { MODALS_ID, QUERY_KEY } from '../../../constants'

import { toast } from '../../../utils'

import { ModalContext } from '../../../provider/ModalProvider'

import { settlement } from '../../../api/my'

import { Button } from '../../../components/button'
import { Modal } from '../../../components/modal'
import { Text } from '../../../components/text'

interface CalculateConfirmModalProps {
  userProductId: number
  rrn: string
  success: () => void
}

export function CalculateConfirmModal({ userProductId, rrn, success }: CalculateConfirmModalProps) {
  const { closeModal } = useContext(ModalContext)

  const queryClient = useQueryClient()
  const mutatingRef = useRef(false)

  const { mutateAsync } = useMutation({
    mutationFn: settlement,
    onSuccess: async () => {
      await queryClient.removeQueries({ queryKey: [QUERY_KEY.userProduct, { userProductId }] })
      await queryClient.removeQueries({ queryKey: [QUERY_KEY.userProductSettle, { userProductId }] })
      success() // navigate
      close()
    },
    onError: (error) => {
      let message = '매각 대금 정산에 실패했어요'

      if (isAxiosError(error)) {
        const responseData = error.response?.data
        const code = responseData?.CODE
        const isDbError = (typeof code === 'string' && code.startsWith('UTIL_001')) || !!responseData?.info?.driverError
        if (isDbError) {
          message = '현재 접속자가 많습니다\n수 초 후 다시 시도해 주세요'
        } else if (responseData?.message) {
          message = responseData.message
        }
      }

      toast(message, error)

      close()
    },
  })

  const close = () => closeModal(MODALS_ID.CALCULATE)

  const calculate = async () => {
    if (dayjs().hour() >= 23 || dayjs().hour() <= 0) {
      toast('은행 점검 시간(23:00 ~ 00:59)이 지난 후에 정산해 주세요.')
      return
    }

    if (mutatingRef.current) return
    mutatingRef.current = true
    try {
      await mutateAsync({ userProductId, data: { rrn } })
    } finally {
      mutatingRef.current = false
    }
  }

  return (
    <Modal id={MODALS_ID.CALCULATE}>
      <Text size={16} weight={500} lineHeight={24} block={true} $textAlign="center">
        매각 대금을 정산할까요?
      </Text>
      <Modal.Footer>
        <Button type="default" onClick={close}>
          아니요
        </Button>
        <Button type="primary" onClick={calculate}>
          네
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
